import axios from 'axios';

// Tracks in-flight retry attempts per (method:url) so a single request is
// retried at most MAX_RETRIES times before we surface a friendly error.
const MAX_RETRIES = 3;
const DEFAULT_RETRY_AFTER = 60; // seconds, used when the server omits Retry-After
const retryQueue = new Map();

// Attaches a 429 (Too Many Requests) handler to an axios instance. On a rate
// limit it waits the Retry-After interval, then re-fires the original request
// (up to MAX_RETRIES). Once exhausted it rejects with a `userMessage` the UI
// can show in a toast instead of letting the caller hammer the backend.
const attachRateLimitRetry = (instance) => {
  instance.interceptors.response.use(
    (response) => {
      const key = `${response.config?.method}:${response.config?.url}`;
      if (retryQueue.has(key)) retryQueue.delete(key);
      return response;
    },
    async (error) => {
      const { response, config } = error;

      if (response?.status === 429 && config) {
        const retryAfter =
          parseInt(response.headers?.['retry-after'], 10) || DEFAULT_RETRY_AFTER;
        const retryKey = `${config.method}:${config.url}`;
        const retryCount = retryQueue.get(retryKey) || 0;

        if (retryCount < MAX_RETRIES) {
          retryQueue.set(retryKey, retryCount + 1);
          await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000));
          // Re-fire through the same instance so this interceptor runs again.
          // The key is cleared on eventual success (fulfilled handler above).
          return instance(config);
        }

        // Max retries hit — stop retrying and surface a friendly message.
        retryQueue.delete(retryKey);
        return Promise.reject(
          Object.assign(error, {
            userMessage: `Too many requests. Please wait ${retryAfter} seconds and try again.`,
          }),
        );
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

// A 401 from these endpoints means bad credentials, not an expired session —
// it must reach the caller so the form can show the error.
const AUTH_ENDPOINTS = ['login', 'register', 'verify-email', 'verify-otp', 'verify-mobile-otp'];

const isAuthEndpoint = (url = '') =>
  AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

// An authenticated session that gets a 401 back is expired or revoked: clear
// it and force a full navigation to '/' so App re-reads localStorage and
// renders <Auth />, instead of stranding staff on a page where every request
// fails silently.
const attachSessionExpiry = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error?.response?.status === 401 &&
        !isAuthEndpoint(error?.config?.url) &&
        localStorage.getItem('user')
      ) {
        localStorage.removeItem('user');
        window.location.href = '/';
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Auth is a Sanctum bearer token sent as an Authorization header. The backend
// removed /sanctum/csrf-cookie and does not run cookie sessions for this
// cross-origin panel (the browser discards its third-party cookies), so no
// withCredentials / CSRF priming — the token from login is the whole session.
const getApi = async (isFormData) => {
  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : null;

  const headers = {
    'Authorization': user?.token ? `Bearer ${user.token}` : undefined,
    'accept': 'application/json',
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10000,
    headers,
  });

  return attachSessionExpiry(attachRateLimitRetry(api));
};

const handleSuccess = (response) => {
  if(response.data?.data){
    return response.data
  } else if(response.data){
    return response
  } else {
    return response
  }
} 

const handleError = (error) => {
  throw error;
};

const convertBooleanToNumber = (data) => {
  if (data && typeof data === 'object') {
    const transformedData = { ...data };
    Object.keys(transformedData).forEach(key => {
      if (typeof transformedData[key] === 'boolean') {
        transformedData[key] = transformedData[key] ? 1 : 0;
      }
    });
    return transformedData;
  }
  return data;
};

const appendFormData = (formData, data, parentKey) => {
  if (data === null || data === undefined) return;

  const valueToAppend = typeof data === 'boolean' ? (data ? 1 : 0) : data;

  if (valueToAppend instanceof File || valueToAppend instanceof Blob) {
    formData.append(parentKey, valueToAppend);
  } else if (Array.isArray(valueToAppend)) {
    // Special handling for skills array - stringify it as most APIs expect
    if (parentKey === 'skills') {
      formData.append(parentKey, JSON.stringify(valueToAppend));
    } else {
      valueToAppend.forEach((item, index) => {
        const arrayKey = parentKey ? `${parentKey}[${index}]` : `${index}`;
        appendFormData(formData, item, arrayKey);
      });
    }
  } else if (typeof valueToAppend === 'object') {
    // Special handling for objects that should be stringified
    if (parentKey === 'secondary_job_hours') {
      formData.append(parentKey, JSON.stringify(valueToAppend));
    } else {
      Object.keys(valueToAppend).forEach((key) => {
        const newKey = parentKey ? `${parentKey}[${key}]` : key;
        appendFormData(formData, valueToAppend[key], newKey);
      });
    }
  } else {
    formData.append(parentKey, valueToAppend);
  }
};

const apiCall = async (method, url, data = null, params = null, isFormData = false) => {
  let response;
  
  try {
    switch (method) {
      case 'GET':
        response = await getApi(false);
        response = await response.get(url, { params });
        break;
        
      case 'POST':
        response = await getApi(isFormData);
        if (isFormData) {
          const formData = new FormData();
          appendFormData(formData, data, '');
          response = await response.post(url, formData);
        } else {
          response = await response.post(url, convertBooleanToNumber(data));
        }
        break;

      case 'PUT':
        response = await getApi(isFormData);
        if (isFormData) {
          const formData = new FormData();
          appendFormData(formData, data, '');
          response = await response.put(url, formData);
        } else {
          response = await response.put(url, convertBooleanToNumber(data));
        }
        break;
        
      case 'PATCH':
        response = await getApi(isFormData);
        if (isFormData) {
          const formData = new FormData();
          appendFormData(formData, data, '');
          response = await response.patch(url, formData);
        } else {
          response = await response.patch(url, convertBooleanToNumber(data));
        }
        break;
        
      case 'DELETE':
        response = await getApi(false);
        response = await response.delete(url);
        break;
        
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
    
    return handleSuccess(response);
  } catch (error) {
    return handleError(error);
  }
};

const parseFilenameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, '');
    }
  }
  const asciiMatch = /filename\s*=\s*("?)([^";]+)\1/i.exec(disposition);
  return asciiMatch?.[2] ? asciiMatch[2].trim() : null;
};

export const downloadFile = async (url, params = null, fallbackFilename = 'download') => {
  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : null;

  const api = attachSessionExpiry(
    attachRateLimitRetry(
      axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        timeout: 60000,
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : undefined,
          Accept: 'application/octet-stream, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, */*',
        },
        responseType: 'blob',
      }),
    ),
  );

  const response = await api.get(url, { params });
  const blob = response.data;

  if (blob && blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      const error = new Error(json?.message || 'Download failed');
      error.response = { data: json, status: response.status };
      throw error;
    } catch (parseErr) {
      if (parseErr instanceof SyntaxError) {
        throw new Error('Download failed');
      }
      throw parseErr;
    }
  }

  const filename =
    parseFilenameFromDisposition(response.headers?.['content-disposition']) ||
    fallbackFilename;

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);

  return { filename };
};

export default apiCall;